import React, { createContext, useContext, useState, ReactNode } from 'react';

export type RecordType = 'vaccine' | 'grooming' | 'treatment' | 'document' | 'note' | 'medical';
export type RecordStatus = 'draft' | 'proposed' | 'approved' | 'rejected';

export interface ProposedRecord {
  id: string;
  petId: string;
  petName: string;
  petDID: string;
  professionalId: string;
  professionalName: string;
  professionalType: string;
  professionalBusinessName: string;
  recordType: RecordType;
  status: RecordStatus;
  data: any; // Flexible data structure based on record type
  submittedDate: string;
  reviewedDate?: string;
  rejectionReason?: string;
  ownerComment?: string;
  supportingDocuments?: string[];
}

interface ProposedRecordsContextType {
  proposedRecords: ProposedRecord[];
  addProposedRecord: (record: Omit<ProposedRecord, 'id' | 'submittedDate' | 'status'>) => void;
  updateRecordStatus: (recordId: string, status: RecordStatus, comment?: string, rejectionReason?: string) => void;
  getProposedRecordsForPet: (petId: string) => ProposedRecord[];
  getProposedRecordsByProfessional: (professionalId: string) => ProposedRecord[];
  getPendingRecordsForOwner: (ownerId: string) => ProposedRecord[];
  requestEdit: (recordId: string, comment: string) => void;
  approveProposal: (recordId: string, comment?: string) => void;
  rejectProposal: (recordId: string, rejectionReason?: string) => void;
}

const ProposedRecordsContext = createContext<ProposedRecordsContextType | undefined>(undefined);

export function ProposedRecordsProvider({ children }: { children: ReactNode }) {
  const [proposedRecords, setProposedRecords] = useState<ProposedRecord[]>([
    // Mock data for testing
    {
      id: 'prop-001',
      petId: '1',
      petName: 'Max',
      petDID: 'did:petso:max:germanshepherd:f7g8h9i0',
      professionalId: 'vet-001',
      professionalName: 'Dr. Sarah Johnson',
      professionalType: 'Veterinarian',
      professionalBusinessName: 'Happy Paws Veterinary Clinic',
      recordType: 'vaccine',
      status: 'proposed',
      data: {
        vaccineName: 'Rabies Booster',
        recommendedDate: '2025-06-15',
        expiryDate: '2026-06-15',
        notes: 'Annual rabies vaccination due. Previous dose administered on 2024-06-15.',
        batch: 'RB-2025-0456',
        manufacturer: 'Zoetis'
      },
      submittedDate: '2025-12-17T10:30:00Z',
      supportingDocuments: []
    },
    {
      id: 'prop-002',
      petId: '1',
      petName: 'Max',
      petDID: 'did:petso:max:germanshepherd:f7g8h9i0',
      professionalId: 'groom-001',
      professionalName: 'Emma Wilson',
      professionalType: 'Groomer',
      professionalBusinessName: 'Pampered Paws Grooming',
      recordType: 'grooming',
      status: 'proposed',
      data: {
        serviceName: 'Full Grooming Package',
        recommendedDate: '2025-12-28',
        services: ['Bath', 'Haircut', 'Nail Trim', 'Ear Cleaning'],
        notes: 'Coat is getting long. Recommend full grooming before the new year.',
        estimatedDuration: '2 hours',
        price: '$85'
      },
      submittedDate: '2025-12-16T14:20:00Z',
      supportingDocuments: []
    }
  ]);

  const addProposedRecord = (record: Omit<ProposedRecord, 'id' | 'submittedDate' | 'status'>) => {
    const newRecord: ProposedRecord = {
      ...record,
      id: `prop-${Date.now()}`,
      submittedDate: new Date().toISOString(),
      status: 'proposed'
    };
    setProposedRecords([...proposedRecords, newRecord]);
  };

  const updateRecordStatus = (recordId: string, status: RecordStatus, comment?: string, rejectionReason?: string) => {
    setProposedRecords(proposedRecords.map(record => {
      if (record.id === recordId) {
        return {
          ...record,
          status,
          reviewedDate: new Date().toISOString(),
          ownerComment: comment,
          rejectionReason: rejectionReason
        };
      }
      return record;
    }));
  };

  const requestEdit = (recordId: string, comment: string) => {
    setProposedRecords(proposedRecords.map(record => {
      if (record.id === recordId) {
        return {
          ...record,
          status: 'draft' as RecordStatus,
          ownerComment: comment
        };
      }
      return record;
    }));
  };

  const approveProposal = (recordId: string, comment?: string) => {
    updateRecordStatus(recordId, 'approved', comment);
  };

  const rejectProposal = (recordId: string, rejectionReason?: string) => {
    updateRecordStatus(recordId, 'rejected', undefined, rejectionReason);
  };

  const getProposedRecordsForPet = (petId: string) => {
    return proposedRecords.filter(record => record.petId === petId);
  };

  const getProposedRecordsByProfessional = (professionalId: string) => {
    return proposedRecords.filter(record => record.professionalId === professionalId);
  };

  const getPendingRecordsForOwner = (ownerId: string) => {
    // In real app, would filter by pets owned by this owner
    return proposedRecords.filter(record => record.status === 'proposed');
  };

  return (
    <ProposedRecordsContext.Provider
      value={{
        proposedRecords,
        addProposedRecord,
        updateRecordStatus,
        getProposedRecordsForPet,
        getProposedRecordsByProfessional,
        getPendingRecordsForOwner,
        requestEdit,
        approveProposal,
        rejectProposal
      }}
    >
      {children}
    </ProposedRecordsContext.Provider>
  );
}

export function useProposedRecords() {
  const context = useContext(ProposedRecordsContext);
  if (context === undefined) {
    throw new Error('useProposedRecords must be used within a ProposedRecordsProvider');
  }
  return context;
}